/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Component, ChangeDetectionStrategy } from "@angular/core";
import { OnInit } from "@angular/core";
import { Input } from "@angular/core";
import { RuiStore } from "@anvilor/ngx-rui";
import { IRuiControlComponent } from "@anvilor/ngx-rui";
import { SubmitButtonControl } from "./submit-button-control";
import { MatActionButtonConfig } from "@anvilor/ngx-apps-core";
import { Observable } from "rxjs";
import { combineLatest } from "rxjs";
import { AnvilorFormsApp } from "../../../store/anvilor-forms-app-store.service";
import { map } from "rxjs/operators";
import { tap } from "rxjs";
import { startWith } from "rxjs";
import { CloseDialogSideEffect } from "./submit-button-control";
import { SubmitButtonSuccessSideEffect } from "./submit-button-control";

@Component({
  selector: "app-submit-button-control",
  templateUrl: "./submit-button-control.component.html",
  styleUrls: ["./submit-button-control.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SubmitButtonControlComponent implements IRuiControlComponent, OnInit {
  @Input() internalId!: string;
  control!: SubmitButtonControl;

  disableButton$?: Observable<boolean>;
  buttonConfig$?: Observable<MatActionButtonConfig>;

  constructor(private rui: RuiStore, public app: AnvilorFormsApp) {}

  ngOnInit() {
    this.control = new SubmitButtonControl(this.rui, this.internalId);

    this.buttonConfig$ = combineLatest([
      this.control.buttonLabel$,
      this.control.payload$,
      this.control.postSubmitSideEffects$.pipe(startWith([])),
    ]).pipe(
      map(([label, payload, sideEffects]) => ({
        label,
        color: "primary",
        keepDialogOpen: true,
        click: () => {
          this.app.submit.doSubmit({
            payload,
            onSuccess: (_) => this.executeSideEffects(sideEffects),
          });
        },
      }))
    );

    this.disableButton$ = combineLatest([
      this.app.submit.isPosting$,
      this.control.useRuiValidation$,
      this.rui.controls.allValid$,
      this.app.openClose.formIsClosed$,
      this.control.additionalDisable$,
      this.app.captcha.canSubmit$,
      this.app.submit.forceEnableSubmit$,
    ]).pipe(
      map(
        ([
          isPosting,
          useRuiValidation,
          ruiAllValid,
          formIsClosed,
          shouldDisable,
          validCaptcha,
          forceEnableSubmit,
        ]) =>
          isPosting ||
          shouldDisable ||
          formIsClosed ||
          (useRuiValidation && !ruiAllValid && !forceEnableSubmit) ||
          !validCaptcha
      )
    );
  }

  private executeSideEffects(sideEffects: SubmitButtonSuccessSideEffect[]) {
    sideEffects?.forEach((sideEffect) => {
      if (sideEffect["action"] === "close_dialog") {
        sideEffect = sideEffect as CloseDialogSideEffect;
        const dialogId = sideEffect["dialog_id"];
        let dialog = this.rui.controls.dialogs[dialogId];
        dialog.close();
      }
    });
  }
}
